import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import {
  RelativePeriod,
  RelativePeriodLabel,
  RelativePeriodMapping,
} from '@components/comparison-period-selector/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { BaseTimePeriod, FilterParam, MainFilters, TimePeriod } from '@services/FiltersTypes';
import { opportunityReviewResponseConfig } from '@utils/FilterSettings';
import {
  areOptionsTheSame,
  checkIfCountryExist,
  checkPGsByCountryPrecondition,
  findDefaultFilterBySelectedCountries,
  getFeatureValueBySubscription,
  getFiltersInitialState,
  getInitialAppliedCountries,
  getInitialSelectedAddFeat,
  getInitialSelectedAddFeatValues,
  getInitialSelectedSegFeat,
  getInitialSelectedSegFeatValues,
  getPeriodForFeatures,
  getSelectedAddFeatValues,
  getSelectedSegFeatValues,
  getSubscriptionByCountryAndProductType,
  isAllFeatureValuesEmpty,
  isAppliedFiltersEqualToSelectedFilters,
  isRemovePricebandFilterForNonLocalCurrency,
  isValidTimeFrameAndPeriod,
  removeUnsavedFilters,
  UpdateAddFeatSegFeatValues,
  updateFieldsForOverviewAndCategoryLeaderboardAndExplore,
  updatePgsByCountryOnCountryApplyWithProductType,
  updatePgsByCountryOnCountrySelectWithProductType,
  updateSelectedProductGroup,
  validateStoreFilterSettings,
} from '@utils/FilterUtils';
import {
  checkPeriodExist,
  getDefaultValueForDerivablePeriodicity,
  getPeriodMetaFactory,
  isBaseTimePeriodFormat,
  isPeriodYTDOrMAT,
  updatePeriodFiltersWhenCountryChange,
} from '@utils/PeriodUtils';
import { isAuthorized } from '@utils/Permission';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  Country,
  FeatureOption,
  FilterKeys,
  Filters,
  Option,
  PageName,
  PageType,
  PageTypeAndName,
} from '@utils/pageConfig/Types';

import { useChartExport } from '@ecosystem/export-utils';
import { ExportFormat, FilterToggleButton, useAnalytics } from '@ecosystem/newron-client-lib';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useExportBubbleChart } from '@hooks/export/export-bubble-chart/UseExportBubbleChart';
import { useExportTable } from '@hooks/export/export-table/UseExportTable';
import { getExportTrackingFormat } from '@hooks/export/export-tracking/ExportTracking';
import {
  constructCountryOption,
  getRetailerDefaultCountryCode,
  getSubscriptionForOtherProducts,
  mapSubscribedCountries,
  onRetailerChange,
} from '@components/multi-banner-retailer-selector/MultiBannerRetailerSelectorUtils';
import { MARKET_REVENUE, RETAILER_REVENUE } from '@components/opportunity-review-filter/OpportunityReviewChartFilter';
import { applyTimeFramePicker } from '@components/time-frame-picker/utils/TimeFramePickerUtils';
import { useToastMessage } from '@components/toast-message/ToastMessage';
import { ClientSubscription } from '@models/Opa';
import {
  BubbleChartParam,
  BubbleChartResp,
  DefaultSortingOptionsForOpportunityReview,
  OpportunityReviewBoardGroupingPgKpi,
  OpportunityReviewTableContent,
  OpportunityReviewTableResponse,
  PageRequest,
  RetailerType,
  SaleType,
  TableParam,
} from '@models/OpportunityReview';
import { RetailerClient } from '@models/RetailerClient';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { getOpportunityReviewBubbleChart } from '@services/Chart.service';
import { getOpportunityReviewTable } from '@services/OpportunityReview.service';
import {
  actionCreators,
  actionCreatorsMarketPremium,
  actionCreatorsRetailerClient,
  actionCreatorsSideFilters,
} from '@state/Index';
import { IRootState } from '@state/reducers/Reducers';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { SideFiltersState } from '@state/reducers/side-filters/SideFiltersReducer';
import { getCountryFilterPairsByTimeFrame } from '@utils/CountryUtils';
import { getExchangeRate } from '@utils/currencyUtils/CurrencyUtils';
import {
  CATEGORY_FEATURE_OPTION,
  constructFeatureValueParam,
  getFeatureSelector,
} from '@utils/hook/selector/ShowPerformanceWithinSelector';
import { ElementType, TrackingGeneralProp } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  dataExportedTrackingFunction,
  DataExportTrackingElementParam,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import {
  PAGE_VIEW_TRACKING_TITLE,
  pageViewTrackingFunction,
  PageViewTrackingFunctionParam,
} from '@utils/page-analytics/model/PageViewedTracking';
import { ProductType } from '../product-analyzer/Product';
import { getVolumeBasedOnPeriodType } from '@utils/factVolumeUtils/FactVolumeUtils';
import useDebouncedEffect from '@hooks/UseDebouncedEffect';
import { useUpdatePerfPeriodSet } from '@utils/period-set/UseUpdatePerfPeriodSet';
import { byCountry } from '@state/reducers/initial-filter/InitialFiltersPredicates';
import { useInitializedPeriodSet } from '@utils/period-set/UseInitializedPeriodSet';
import { MainFilterStateProducts } from '@state/reducers/main-filter-products/MainFiltersProductsReducer';
import { opportunityReviewConfiguration } from '../Configuration';
import { OpportunityReviewRestoreLocalSavedViewProps, OpportunityReviewViewState } from '@models/SavedViews';
import { capitalizeFirstLetter } from '@utils/StringUtils';
import { useRestoreOpportunityReviewSavedView } from '@utils/hook/saveViews/UseRestoreOpportunityReviewSavedView';
import { checkMATOrYTDPeriodicity } from '@components/time-frame-picker/components/wrapper/WrapperUtils';
import { layouts } from '@ecosystem/newron-design-system';
import saveErrorLog, { ErrorTagEnum } from '../../utils/LogUtil';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { OpportunityReviewBubbleChartInsight } from '@components/collections/opportunity-review-bubble-chart/Model';
import { getGrandTotalValue, GRAND_TOTAL } from '@utils/TotalUtils';
import { useUserPersistedFilters } from '@utils/hook/saveFilter/UseUserPersistedFilters';
import { useDispatchPgsAvailability } from '@utils/hook/incrementalLoading/UseDispatchPgsAvailability';
import { getProductGroupsMasterByCountry } from '@services/ProductGroup.service';
import { cloneDeep } from 'lodash';
import { getLatestPeriodAmongAppliedCountries } from '@utils/multiCountryUtils/MultiCountryUtils';
import { shouldLimitProductGroup } from '@utils/exploreUtils/ExploreUtils';
import { useSideMenuFilters } from '@utils/hook/sideFilters/SideFiltersHooks';
import { NO_OPTIONS } from '@utils/StringConstants';
import { getFilteredFeatureValues } from '@utils/FilterFeatureFromDropdown';
import { updateCountryPgSideFilter } from '@utils/UpdateCountryPgSideFilter';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { GroupContext } from '@utils/userPreferencesUtils/Types';
import { useOpportunityReviewGroupState } from '@utils/hook/userPreferences/UseGroupState';
import PageHeader from '@components/page-header/PageHeader';
import PageInfo from '@pages/info/PageInfo';
import OpportunityReviewCopyViewButton from '@components/opportunity-review-copy-button/OpportunityReviewCopyViewButton';
import InfoModal from '@components/info-modal/InfoModal';
import LoadingOverlay from '@components/loading-overlay/LoadingOverlay';
import TimeFramePickerWrapper from '@components/time-frame-picker/components/wrapper/TimeFramePickerWrapper';
import ComparisonGrowthSelector from '@components/comparison-growth-selector/ComparisonGrowthSelector';
import FilterSelectionNote from '@components/filter-selection-note/FilterSelectionNote';
import PgAvailabilityBanner from '@components/pg-availability-banner/PgAvailabilityBanner';
import FilterChips from '@components/filter-chips/FilterChips';
import OpportunityReviewBoardGroupProvider from '@components/opportunity-review-group/utils/OpportunityReviewBoardGroupProvider';
import OpportunityReviewCard from '@components/opportunity-review-card/OpportunityReviewCard';
import { resizeChart } from '@data-visualization/utilities/ChartUtils';
import { OpportunityReviewBoardActionBar } from '@components/opportunity-review-group/OpportunityReviewBoardActionBar';
import { CreateOpportunityReviewBoardModalGroup } from '@components/opportunity-review-group/create-group/CreateOpportunityReviewBoardModalGroup';
import { ShareOpportunityReviewBoardModalGroup } from '@components/opportunity-review-group/share-group/ShareOpportunityReviewBoardModalGroup';
import { DeleteOpportunityReviewBoardModalGroup } from '@components/opportunity-review-group/delete-group/DeleteOpportunityReviewBoardModalGroup';
import { Unauthorized } from '@pages/unauthorized/Unauthorized';
import { useOpportunityReviewChartDownload } from '@hooks/export/UseOpportunityReviewChartDownload';
import useFeatureValueByPGAndCountry from '@utils/hook/sideFilters/UseFeatureValueByPGAndCountry';
import useLimitProductGroup from '@utils/hook/explore/UseLimitProductGroup';
import { initialiseBaseFilters } from '@utils/InitBaseFilters';
import { formatPriceBandAsPerExchangeRate } from '@utils/Formatters';
import { getCurrencyExchangeRate } from '@services/Currency.service';
import { useFactRevenueBySelectedCurrency } from '@hooks/UseFactRevenueBySelectedCurrency';
import { setApiErrorState } from '@services/utils/ErrorHandlers';

const DEFAULT_BUBBLE_CHART_RESPONSE = new BubbleChartResp();

interface Props {
  retailerClients: RetailerClient[];
}

export const getGroupedDataRowsForExport = (
  response: OpportunityReviewTableResponse,
): OpportunityReviewTableContent[] => {
  return (response.groupContent ?? []).reduce<OpportunityReviewTableContent[]>((acc, group) => {
    acc.push(...group.groupKpis.filter((groupKpi) => groupKpi.segment !== GRAND_TOTAL));
    return acc;
  }, []);
};

const constructEmptyTableRowForOpportunityReview = (grouping: string): OpportunityReviewTableContent => {
  return {
    segment: 'GRAND_TOTAL',
    marketRevenue: { value: null!, actual: null!, percentage: null! },
    marketVolume: { value: null!, actual: null!, percentage: null! },
    myRevenue: { value: null!, actual: null!, percentage: null! },
    myVolume: { value: null!, actual: null!, percentage: null! },
    myShareRevenue: { growthPercentage: null!, sharePercentage: null! },
    myShareVolume: { growthPercentage: null!, sharePercentage: null! },
    growthPerformanceRevenue: '',
    growthPerformanceVolume: '',
    fairSharePerformanceRevenue: '',
    fairSharePerformanceVolume: '',
    growthOppRevenue: { value: null! },
    growthOppVolume: { value: null! },
    fairShareOppRevenue: { value: null! },
    fairShareOppVolume: { value: null! },
    swotCategoryRevenue: { value: '', swotStrength: '' },
    swotCategoryVolume: { value: '', swotStrength: '' },
    grouping,
  };
};

export const getSummaryDataRowsForExport = (
  response: OpportunityReviewTableResponse,
): OpportunityReviewTableContent[] => {
  const groupsWithEmptyKpis: OpportunityReviewBoardGroupingPgKpi[] = response.groupContent!.filter((group) =>
    group.groupKpis.length === 0
      ? group.groupKpis.push(constructEmptyTableRowForOpportunityReview(group.groupDetails.groupName))
      : group.groupKpis,
  );

  // Edge case when groupName is equal to segment
  const groupsTotal: OpportunityReviewTableContent[] = [];
  for (const group of groupsWithEmptyKpis) {
    const groupTotal: OpportunityReviewTableContent = group.groupKpis.find(
      (kpi) => kpi.segment === getGrandTotalValue(),
    )!;
    groupTotal.segment = group.groupDetails.groupName;
    groupsTotal.push(groupTotal);
  }

  return [...response.content!.content.splice(0, 1), ...groupsTotal];
};

const supportedPeriodicities: Periodicity[] = opportunityReviewConfiguration.supportedPeriodicities;
const OpportunityReview: React.FC<Props> = ({ retailerClients }) => {
  const CURRENT_TOTAL_NAME = getGrandTotalValue();
  const pagesConfig = usePageConfig();
  const { pgsByAppliedCountry, masterPgsByCountry } = pagesConfig;
  const { refetch } = useUserPersistedFilters();
  const { Page } = layouts;
  const isAuthorizedUser = isAuthorized(pagesConfig.clientSubscription);
  const { track } = useAnalytics();

  /*
   * Redux
   */
  const dispatch = useDispatch();
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);
  const { updateRetailerClient } = bindActionCreators(actionCreatorsRetailerClient, dispatch);
  const { updateMainFiltersMarketPremium } = bindActionCreators(actionCreatorsMarketPremium, dispatch);

  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.OPPORTUNITY_REVIEW);

  const initialFiltersState = useSelector((state: IRootState) => state.initialFilters);
  const mainFiltersState: MainFiltersState = useSelector((state: IRootState) => state.mainFilters);
  const sideFiltersState: SideFiltersState = useSelector((state: IRootState) => state.sideFilters);
  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);
  const initialFiltersProductsState = useSelector((state: IRootState) => state.initialFiltersProducts);
  const mainFilterStateProducts: MainFilterStateProducts = useSelector(
    (state: IRootState) => state.mainFiltersProducts,
  );
  const hitlistsRetailerClientState = useSelector((state: IRootState) => state.hitlistsRetailerClient);
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);

  /*
   * Get initial state for side menu filters
   */
  const allFilters = [...pagesConfig.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters];
  const pgInitialState = getFiltersInitialState(allFilters, FilterKeys.PRODUCT_GROUP);
  const addFeatInitialState =
    pgInitialState && pgInitialState.options.length < 2
      ? getFiltersInitialState(allFilters, FilterKeys.ADDFEAT)
      : undefined;
  const segFeatInitialState =
    pgInitialState && pgInitialState.options.length < 2
      ? getFiltersInitialState(allFilters, FilterKeys.SEGFEAT)
      : undefined;

  // Stores the selected product group
  // const [selectedProductGroup, setSelectedProductGroup] = useState<Option[] | undefined>(
  //   pgInitialState !== undefined ? pgInitialState.options : undefined,
  // );

  const [selectedProductGroups, setSelectedProductGroups] = useState<Option[] | undefined>(pgInitialState?.options);

  const [defaultFilters, setDefaultFilters] = useState<MainFilters[]>(initialFiltersState.initialFilters);

  const [selectedFeatureOption, setSelectedFeatureOption] = useState<FeatureOption>();

  const [selectedSortingOption, setSelectedSortingOption] = useState<SortingOptions>(
    DefaultSortingOptionsForOpportunityReview,
  );

  // Stores the applied product group. APIs use this state as query param
  const [appliedProductGroups, setAppliedProductGroups] = useState<Option[]>(
    pgInitialState !== undefined ? pgInitialState.options : [],
  );

  const { defaultCountry: initialCountry, defaultCurrency: initialCurrency } = initialiseBaseFilters(
    pagesConfig,
    effectiveProductType,
    updateSideFilters,
    PageType.OPPORTUNITY_REVIEW,
  );

  const [appliedCountries, setAppliedCountries] = useState<Option[]>(initialCountry);

  const [selectedCountries, setSelectedCountries] = useState<Option[]>(initialCountry);

  const isMultiCountry = appliedCountries.length > 1;

  const [filters, setFilters] = useState<MainFilters>(
    findDefaultFilterBySelectedCountries(
      pagesConfig.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters,
      defaultFilters,
    ),
  );
  const [displayTable, setDisplayTable] = useState<boolean>(true);
  const [isFetchingApi, setIsFetchingApi] = useState<boolean>(false);

  // options in the feature selector
  const [allFeatureValuesOptions, setAllFeatureValuesOptions] = useState<Filters[]>([]);
  const [isUpdateFeaturesApiCall, setIsUpdateFeaturesApiCall] = useState<boolean>(false);

  const [isSubscribed, setIsSubscribed] = useState<boolean>(true);

  const [onPageFirstLoad, setOnPageFirstLoad] = useState<boolean>(true);
  const [onContextBarCompleted] = useState<boolean>(false);

  const [isCountryFirstLoad, setIsCountryFirstLoad] = useState<boolean>(true);

  const getDefaultGrowth = () => {
    return mainFiltersState.selectedGrowthType;
  };

  const [areDependenciesUpdated, setAreDependenciesUpdated] = useState<boolean>(false); //This state group all the dependencies that needed for DATA api call and prevent multiple dependencies triggered.

  /*
   * 5 Main Filters - Feature, TimeFrame, TimePeriod, ComparisonPeriod, CompareIn
   */
  const [feature, setFeature] = useState<FeatureOption>(mainFiltersState.feature);

  const [selectedGrowthType, setSelectedGrowthType] = useState<GrowthTypes>(getDefaultGrowth());
  const [doPreserveSelections, setDoPreserveSelections] = useState(false);

  const updatedInitialFiltersState = useMemo(
    () => ({
      ...initialFiltersState,
      initialFilters: initialFiltersState.initialFilters.map((filter) => ({
        ...filter,
        timePeriod: filter.timePeriod,
      })),
    }),
    [initialFiltersState],
  );

  //region variables and setters related to period set
  const periodDispatchActions = useUpdatePerfPeriodSet();
  const timePeriodOptions: TimePeriod = useMemo(() => {
    return isMultiCountry && appliedCountries.length > 1
      ? getLatestPeriodAmongAppliedCountries(appliedCountries, updatedInitialFiltersState)
      : updatedInitialFiltersState.initialFilters.filter(byCountry(appliedCountries[0].value))[0].timePeriod;
  }, [appliedCountries, initialFiltersState, isMultiCountry]);
  const { period, periodicity, comparisonPeriod, customMonthsPeriodState, customWeeksPeriodState } =
    useInitializedPeriodSet(timePeriodOptions);
  //endregion

  const [selectedAddFeatOpp, setSelectedAddFeatOpp] = useState<string | undefined>(
    getInitialSelectedAddFeat(addFeatInitialState),
  );

  const [selectedAddFeatOppValues, setSelectedAddFeatOppValues] = useState<Option[] | undefined>(
    getInitialSelectedAddFeatValues(addFeatInitialState),
  );

  const [selectedSegFeatOpp, setSelectedSegFeatOpp] = useState<string | undefined>(
    getInitialSelectedSegFeat(segFeatInitialState),
  );

  const [selectedSegFeatOppValues, setSelectedSegFeatOppValues] = useState<Option[] | undefined>(
    getInitialSelectedSegFeatValues(segFeatInitialState),
  );

  const [isApiError, setIsApiError] = useState<boolean>(false);
  const [bubbleChartData, setBubbleChartData] = useState(DEFAULT_BUBBLE_CHART_RESPONSE);

  const [tableData, setTableData] = useState<OpportunityReviewTableContent[]>([]);
  const [tableGroupData, setTableGroupData] = useState<OpportunityReviewBoardGroupingPgKpi[]>([]);

  const [saleType, setSaleType] = useState<SaleType>(SaleType.VALUE);
  const [retailerType, setRetailerType] = useState<RetailerType>(RetailerType.MARKET_REVENUE);
  //To get the latest formatted selected period from Auto Slider while exporting bubble chart.
  //The currentPeriod state in OpportunityReviewCard also has the latest selected period except the formatting is different.
  //Ex: currentPeriod = DEC 22, bubbleSliderCurrentPeriod= Dec 2022
  const [bubbleSliderCurrentPeriod, setBubbleSliderCurrentPeriod] = useState<string>('');

  const [loadingBubbleChart, setLoadingBubbleChart] = useState<boolean>(false);
  const [loadingSwotTable, setLoadingSwotTable] = useState<boolean>(false);
  const [loadingOverlay, setLoadingOverlay] = useState<boolean>(false);

  const [loadMore, setLoadMore] = useState<boolean>(false);
  const defaultPageNumber = 0;
  const defaultTableSize = 10;
  const [currentPage, setCurrentPage] = useState<number>(defaultPageNumber);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [countryChanged, setCountryChanged] = useState<boolean>(false);

  const [swotTableParam, setSwotTableParam] = useState<TableParam>();
  const [swotTableTotalSize, setSwotTableTotalSize] = useState<number>(0); //This state is to store the total size of Swot in order to get all the data while exporting Table data.
  const { showToastMessage } = useToastMessage();

  const [swotChartParam, setSwotChartParam] = useState<BubbleChartParam>();

  const [showPerformanceFeatureList, setShowPerformanceFeatureList] = useState<Filters[]>(
    cloneDeep(allFeatureValuesOptions),
  );
  const { data: allFeatureValues } = useSideMenuFilters({
    clientId: pagesConfig.clientId,
    timeFrame: getDefaultValueForDerivablePeriodicity(periodicity),
    selectedCountries: selectedCountries,
    selectedProducts: selectedProductGroups,
    appliedProductGroups,
    responseConfig: opportunityReviewResponseConfig,
    pagesConfig,
    initialFilters: initialFiltersState.initialFilters,
    currentTotalName: CURRENT_TOTAL_NAME,
    productType: effectiveProductType,
  });

  //Grouping
  const context: GroupContext = useMemo(() => {
    return {
      client: retailerClientState.clientName,
      country: getRetailerDefaultCountryCode(retailerClientState, appliedCountries),
      isCategory: feature?.value === 'Category',
    };
  }, [retailerClientState, appliedCountries, appliedProductGroups, feature]);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const { allGroupsForApiCalls, getOpportunityReviewGroup, getNewSortValues } = useOpportunityReviewGroupState(context);

  const [updatedFeatureFilters, setUpdatedFeatureFilters] = useState<Filters[]>(allFeatureValues);
  const myPagesConfigAllFilters = [...pagesConfig.appliedFilters];
  const isLastPage = () => {
    return currentPage === totalPage - 1;
  };

  const onClick = (isExpanded: boolean) => {
    pagesConfig.toggleFiltersTray(isExpanded);
    resetSideFilter();
  };

  const resetSideFilter = () => {
    pagesConfig.setApplyFilters(false);
    pagesConfig.setClearFilters(false);
  };

  const countriesToQuery = (countries: Option[]) => {
    return pagesConfig.clientCountries.filter((x) => countries.some((y) => x.id === y.value));
  };

  const removeFeatureFromDropdown = (currentFeatureValues: Filters[]): Filters[] => {
    return getFilteredFeatureValues(currentFeatureValues, pagesConfig, effectiveProductType);
  };
  // Get period from redux
  const getSelectedPeriodForBubbleChart = () => {
    if (isPeriodYTDOrMAT(period)) {
      return {
        currentPeriod: period.value.substring(4),
        comparisonPeriod: comparisonPeriod,
      };
    }
    const formattedPeriod = getPeriodMetaFactory(period.value, comparisonPeriod as RelativePeriod, periodicity);
    return {
      currentPeriod: formattedPeriod!.currentPeriod,
      comparisonPeriod: formattedPeriod!.comparisonPeriod,
    };
  };

  useDispatchPgsAvailability({
    periodValue: period.value,
    periodicity,
    appliedCountries,
    pgsByAppliedCountry,
    countriesPgsAvailabilities: masterPgsByCountry,
    appliedProductGroups,
    isMultiCountry,
    feature,
  });

  const appliedFilters = [...pagesConfig.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters];
  const hasSelectedCountry = useMemo(
    () => appliedFilters.find((f) => f.filterKey === FilterKeys.COUNTRY)?.options,
    [appliedFilters],
  );

  /**
   * Refreshes Country and PG filter options in side menu filter
   */
  useEffect(() => {
    (async () => {
      await updateCountryPgSideFilter({
        retailerClientState,
        periodicity,
        appliedCountries,
        selectedCountries,
        effectiveProductType,
        pagesConfig,
        responseConfig: opportunityReviewResponseConfig,
      });
    })();
  }, [periodicity, appliedCountries, retailerClientState.clientId, effectiveProductType, hasSelectedCountry]);

  // NEO-25267: Once Incremental Loading changes have gone mainstream, we may REMOVE this hook
  const periodOptions = filters?.timePeriod;
  const getBubbleChartResponse = async (featureOption: FeatureOption) => {
    const saleSelector = document
      .getElementById('sale-selector')
      ?.querySelector('[role="button"] > span:first-child')
      ?.textContent.trim()
      .toLowerCase();

    const saleTypeString = getVolumeBasedOnPeriodType(filters, periodicity)?.toLowerCase();
    const getSaleType = saleSelector === saleTypeString ? SaleType.VOLUME : SaleType.VALUE;

    const bubbleChartParam: BubbleChartParam = {
      clientId: retailerClientState.clientId,
      productType: effectiveProductType,
      productGroup: appliedProductGroups ?? [],
      country: countriesToQuery(appliedCountries),
      period: period.value,
      comparisonPeriod: comparisonPeriod as RelativePeriod,
      periodType: periodicity,
      saleType: getSaleType,
      segmentDataType: featureOption.dataType,
      segmentName: featureOption.feature,
      appliedFilters: appliedFilters,
      isSummary: false,
    };
    setSwotChartParam(bubbleChartParam);

    try {
      setIsApiError(false);
      setLoadingBubbleChart(true);
      const data = await getOpportunityReviewBubbleChart(bubbleChartParam);
      setBubbleChartData(data);
    } catch (error) {
      saveErrorLog(error, 'Opportunity Review - Unable to get bubble chart service.', {
        category: ErrorTagEnum.ApiError,
        requestPayload: {
          clientId: retailerClientState.clientId,
          productGroup: appliedProductGroups,
          period: period.value,
          comparisonPeriod: comparisonPeriod,
        },
      });
      setBubbleChartData(DEFAULT_BUBBLE_CHART_RESPONSE);
      setApiErrorState(error, setIsApiError);
    } finally {
      setIsFetchingApi(false);
      setLoadingBubbleChart(false);
      setAreDependenciesUpdated(false);
    }
  };

  const prepareGetTableRequest = (featureOption: FeatureOption, currentSortingOption: SortingOptions) => {
    const appliedFilters = [...pagesConfig.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters];

    const tableParam: TableParam = {
      clientId: retailerClientState.clientId,
      productType: effectiveProductType,
      productGroup: appliedProductGroups,
      countryIds: [countriesToQuery(appliedCountries)[0].id],
      period: period.value,
      comparisonPeriod: comparisonPeriod,
      periodType: periodicity,
      pageRequest: { page: defaultPageNumber, size: defaultTableSize } as PageRequest,
      sortOption: currentSortingOption,
      growthType: selectedGrowthType,
      appliedFilters: appliedFilters,
      segment: featureOption.feature,
      dataType: featureOption.dataType,
      oRGroups: allGroupsForApiCalls,
    };
    setSwotTableParam(tableParam);

    return tableParam;
  };

  const getTableResponse = async (featureOption: FeatureOption, previousSortingOption: SortingOptions) => {
    let currentSortingOption = pagesConfig.sortingOptionsOpportunityReview;

    if (selectedFeatureOption?.value !== featureOption.value) {
      currentSortingOption = previousSortingOption;
    }
    setLoadingSwotTable(true);
    setIsApiError(false);
    const data = await getOpportunityReviewTable(prepareGetTableRequest(featureOption, currentSortingOption));

    if (data !== null) {
      setCurrentPage(defaultPageNumber);
      setTotalPage(data.content.totalPages);
      setSwotTableTotalSize(data.content.totalElements);
      setTableData(data.content.content);
      setSelectedSortingOption(currentSortingOption);
      setSelectedFeatureOption(featureOption);
      if (data.groupContent) {
        setTableGroupData(data.groupContent);
      }
    } else {
      setTableData([]);
      setSwotTableTotalSize(0);
      setDoPreserveSelections(false);
    }

    setIsFetchingApi(false);
    setLoadingSwotTable(false);
    setAreDependenciesUpdated(false);
  };

  const getGroupingTableResponse = async (featureOption: FeatureOption, previousSortingOption: SortingOptions) => {
    let currentSortingOption = pagesConfig.sortingOptionsOpportunityReview;

    if (selectedFeatureOption?.value !== featureOption.value) {
      currentSortingOption = previousSortingOption;
    }
    const data = await getOpportunityReviewTable(prepareGetTableRequest(featureOption, currentSortingOption));

    if (data !== null) {
      if (data.groupContent) {
        setTableGroupData(data.groupContent);
      }
    } else {
      setTableGroupData([]);
    }

    setIsFetchingApi(false);
  };

  const isValidAppliedProductGroup = () => {
    return appliedProductGroups;
  };

  //Export Functionalities for Table & Bubble Chart
  //disable export button while the data is still loading/ data changed for respective view
  const [isExportButtonDisabled, setIsExportButtonDisabled] = useState<boolean>(false);
  useEffect(() => {
    const isViewLoadingBubbleChart = !displayTable && loadingBubbleChart;
    const isViewLoadingSwotTable = displayTable && loadingSwotTable;
    setIsExportButtonDisabled(isViewLoadingBubbleChart || isViewLoadingSwotTable); //NOSONAR typescript:S6606
  }, [
    loadingSwotTable,
    loadingBubbleChart,
    bubbleChartData,
    tableData,
    displayTable,
    getOpportunityReviewGroup.isLoading,
  ]);

  const { totalSubscriptionPgs } = useSelector((state: IRootState) => state.availabilityPg);
  const isLimitPG = useLimitProductGroup(appliedProductGroups.length, feature);

  useEffect(() => {
    const fetchFeatureValues = async () => {
      setIsApiError(false);

      if (isLimitPG) {
        // reset all data & pagination & dropdown
        const newFeatureFilters = removeFeatureFromDropdown(allFeatureValues);
        setUpdatedFeatureFilters(newFeatureFilters);
        setLoadingBubbleChart(false);
        setLoadingSwotTable(false);
        setBubbleChartData(DEFAULT_BUBBLE_CHART_RESPONSE);
        setTableData([]);
        setSwotTableTotalSize(0);
        setCurrentPage(defaultPageNumber);
        setIsFetchingApi(false);
        setAreDependenciesUpdated(false);
        setDoPreserveSelections(false);
        return;
      }

      if (!isAllFeatureValuesEmpty(allFeatureValues)) {
        const newFeatureFilters = removeFeatureFromDropdown(allFeatureValues);
        setUpdatedFeatureFilters(newFeatureFilters);

        const showPerformanceWithinOption = getFeatureSelector(
          newFeatureFilters,
          feature,
          [],
          PageName.OPPORTUNITY_REVIEW,
        );
        setFeature(showPerformanceWithinOption);

        await getBubbleChartResponse(showPerformanceWithinOption);
        await getTableResponse(showPerformanceWithinOption, selectedSortingOption);
      } else if (allFeatureValues.length > 0 && isAllFeatureValuesEmpty(allFeatureValues)) {
        setDataStateToDefault();
      }
    };
    pagesConfig.setIsCountryChanged(false);
    if (areDependenciesUpdated) {
      fetchFeatureValues();
    } else {
      const newFeatureFilters = removeFeatureFromDropdown(allFeatureValues);
      setUpdatedFeatureFilters(newFeatureFilters);
    }
  }, [areDependenciesUpdated, allFeatureValues]);

  const [invokeExport, onChartExportReady] = useChartExport();
  const { download: exportTable } = useExportTable([]);
  const { download: exportBubbleChart } = useExportBubbleChart();
  const handleDataExportTrackingFunction = useDataExportTracking('mainFilters');

  const handleExportedTracking = (exportFormats: ExportFormat[], elementKPI: string) => {
    const dataExportedTrackingParam: DataExportTrackingElementParam = {
      elementType: !displayTable ? ElementType.BUBBLE_CHART : ElementType.TABLE,
      elementKpi: elementKPI,
      elementSort: pagesConfig.sortingOptionsOpportunityReview,
      optionChosen: exportFormats.map((format) => getExportTrackingFormat(format)),
    };
    handleDataExportTrackingFunction(dataExportedTrackingParam, dataExportedTrackingFunction);
  };

  const getSegmentBy = (inputRetailerType: string): string => {
    return inputRetailerType === RetailerType.MARKET_REVENUE ? MARKET_REVENUE : RETAILER_REVENUE;
  };

  const factRevenueBySelectedCurrency = useFactRevenueBySelectedCurrency(
    pagesConfig,
    appliedCountries,
    effectiveProductType,
  );

  const { onChartDownload } = useOpportunityReviewChartDownload({
    swotTableParam,
    swotChartParam,
    swotTableTotalSize,
    displayTable,
    saleType,
    bubbleSliderCurrentPeriod,
    comparisonPeriod,
    periodicity,
    period,
    exportTable,
    exportBubbleChart,
    invokeExport,
    appliedCountries,
    appliedProductGroups,
    feature,
    filters,
    pagesConfig,
    handleExportedTracking,
    tableData,
    getSummaryDataRowsForExport,
    getGroupedDataRowsForExport,
    getSegmentBy,
    retailerType,
    bubbleChartData,
    showToastMessage,
    factRevenue: factRevenueBySelectedCurrency,
  });

  const toUpdateAddFeatSegFeatValues: UpdateAddFeatSegFeatValues = {
    setSelectedAddFeat: setSelectedAddFeatOpp,
    setSelectedAddFeatValues: setSelectedAddFeatOppValues,
    setSelectedSegFeat: setSelectedSegFeatOpp,
    setSelectedSegFeatValues: setSelectedSegFeatOppValues,
  };

  useEffect(() => {
    updateSelectedProductGroup(
      pagesConfig,
      selectedCountries,
      setSelectedCountries,
      setSelectedProductGroups,
      toUpdateAddFeatSegFeatValues,
    );
  }, [pagesConfig.selectedFilters]);

  useEffect(() => {
    updateFieldsForOverviewAndCategoryLeaderboardAndExplore(
      pagesConfig,
      selectedCountries,
      setSelectedCountries,
      setSelectedProductGroups,
      updateSideFilters,
    );
  }, [pagesConfig.selectedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters]);

  useEffect(() => {
    const pageTypeAndName: PageTypeAndName = {
      pageType: PageType.OPPORTUNITY_REVIEW,
      pageName: PageName.OPPORTUNITY_REVIEW,
    };

    //Update page viewing
    updateRetailerClient({ ...retailerClientState, pageViewing: PageName.OPPORTUNITY_REVIEW });

    if (
      isRemovePricebandFilterForNonLocalCurrency(
        pagesConfig,
        sideFiltersState.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters,
        effectiveProductType,
        feature,
      ) ||
      feature.dataType === NO_OPTIONS
    ) {
      setFeature(CATEGORY_FEATURE_OPTION); // reset the feature
    }

    pagesConfig.setPageType(PageType.OPPORTUNITY_REVIEW);
    pagesConfig.setPageName(PageName.OPPORTUNITY_REVIEW);

    pagesConfig.setSelectedFilters(myPagesConfigAllFilters);
    validateStoreFilterSettings(sideFiltersState, pagesConfig, pageTypeAndName);

    pagesConfig.setIsNavigateFromRS(true);
    pagesConfig.setIsHidePGInfoWarning(false);
    pagesConfig.setClearFilters(false);
    pagesConfig.setSelectedTimePeriod(period);

    updateFieldsForOverviewAndCategoryLeaderboardAndExplore(
      pagesConfig,
      appliedCountries,
      setSelectedCountries,
      setSelectedProductGroups,
      updateSideFilters,
    );

    // To ensure getting the correct timeFrame and period when first load due to race conditions issue that we have right now
    setOnPageFirstLoad(false);
    //Start Page View Amplitude Tracking
    //Do not load this amplitude tracking on very first load
    if (appliedCountries.length !== 0 && appliedProductGroups !== undefined && appliedProductGroups?.length !== 0) {
      const trackingParam: PageViewTrackingFunctionParam = {
        eventTitle: PAGE_VIEW_TRACKING_TITLE,
        sideFilters: sideFiltersState.appliedFilters,
        retailerClientState: retailerClientState,
        track: track,
        pageType: PageType.OPPORTUNITY_REVIEW, // Won't get from redux because of async. RO & CL page sharing the same pageType in pagesConfig
        pageName: PageName.OPPORTUNITY_REVIEW, // Won't get from redux because of async
        access: pagesConfig.access,
        initialLocationId: getInitialAppliedCountries(pagesConfig, PageType.OPPORTUNITY_REVIEW, effectiveProductType),
      };
      pageViewTrackingFunction(trackingParam);
    }

    // To remove state of the selectedBrandBubble in Bubble chart once page unmounted.
    return () => {
      updateMainFilters({
        selectedBrandBubble: undefined,
      });
    };
  }, []);

  useEffect(() => {
    //Start Page View Amplitude Tracking
    //Make initial amplitude load after context bar is completed
    if (onContextBarCompleted) {
      const trackingParam: PageViewTrackingFunctionParam = {
        eventTitle: PAGE_VIEW_TRACKING_TITLE,
        sideFilters: sideFiltersState.appliedFilters,
        retailerClientState: retailerClientState,
        track: track,
        pageType: PageType.OPPORTUNITY_REVIEW, // Won't get from redux because of async. RO & CL page sharing the same pageType in pagesConfig
        pageName: PageName.OPPORTUNITY_REVIEW, // Won't get from redux because of async
        access: pagesConfig.access,
        initialLocationId: getInitialAppliedCountries(pagesConfig, PageType.OPPORTUNITY_REVIEW, effectiveProductType),
      };
      pageViewTrackingFunction(trackingParam);
    }
    //End Page View Amplitude Tracking
  }, [onPageFirstLoad, onContextBarCompleted]);

  useEffect(() => {
    const payload = {
      selectedGrowthType: selectedGrowthType,
      ...(feature !== CATEGORY_FEATURE_OPTION && { feature: feature }),
    };

    const mainFilters = {
      timeFrame: periodicity,
      period,
      comparisonPeriod,
      selectedGrowthType,
      feature,
    };
    updateMainFiltersMarketPremium({
      ...mainFilters,
    });

    updateMainFilters(payload);
  }, [feature, selectedGrowthType]);

  /*
   * Fetch product groups for the side menu filter when filter tray is opened
   */
  useEffect(() => {
    const clientId = retailerClientState.clientId;
    if (
      !checkPGsByCountryPrecondition(pagesConfig, clientId, periodicity, myPagesConfigAllFilters, selectedCountries)
    ) {
      return;
    }

    const filter = opportunityReviewResponseConfig.response[0];
    updatePgsByCountryOnCountrySelectWithProductType(
      pagesConfig,
      clientId,
      periodicity,
      filter,
      selectedCountries,
      effectiveProductType,
    );
  }, [selectedCountries, pagesConfig.pgsByPage, pagesConfig.pageType]);

  /*
      useEffect that updates period set & filter
      When the applied countries is changed,
      pgs-by-focus-period and product-group API is triggered
      */
  useEffect(() => {
    (async () => {
      const clientId = retailerClientState.clientId;
      if (!isCountryFirstLoad) {
        const updatePeriodParamsORPA = {
          givenPeriodCombo: { period, periodicity, comparisonPeriod },
          supportedPeriodicities,
          pagesConfig,
          appliedCountries,
          selectedCountries,
          defaultFilters,
          updatePeriod: periodDispatchActions.updatePeriod,
          updatePeriodicity: periodDispatchActions.updatePeriodicity,
          updateComparisonPeriod: periodDispatchActions.updateComparisonPeriod,
          updateCustomMonthsPeriodState: periodDispatchActions.updateCustomMonthsPeriodState,
          updateCustomWeeksPeriodState: periodDispatchActions.updateCustomWeeksPeriodState,
          setFilters,
          setCountryChanged,
          customMonthsPeriod: customMonthsPeriodState,
          customWeeksPeriod: customWeeksPeriodState,
        };
        updatePeriodFiltersWhenCountryChange(updatePeriodParamsORPA);
      }
      setIsCountryFirstLoad(false);

      const availableMasterPgs = await getProductGroupsMasterByCountry(
        clientId,
        appliedCountries,
        effectiveProductType,
      );
      pagesConfig.setMasterPgsByCountry(availableMasterPgs);

      if (
        !checkPGsByCountryPrecondition(pagesConfig, clientId, periodicity, myPagesConfigAllFilters, appliedCountries)
      ) {
        return;
      }
      updatePgsByCountryOnCountryApplyWithProductType(
        pagesConfig,
        clientId,
        selectedCountries,
        appliedCountries,
        effectiveProductType,
        undefined,
      );
    })();
  }, [pagesConfig.pgsByCountry]);

  useEffect(() => {
    const periodTimeFrame: Periodicity = getPeriodForFeatures(pagesConfig, periodicity, selectedCountries);
    getSelectedAddFeatValues({
      productType: effectiveProductType,
      selectedProductGroup: selectedProductGroups,
      selectedAddFeatValues: selectedAddFeatOppValues,
      selectedAddFeat: selectedAddFeatOpp,
      initialFilters: initialFiltersState.initialFilters,
      selectedCountries,
      pagesConfig,
      selectedTimeFrame: periodTimeFrame,
    });
  }, [selectedAddFeatOppValues]);

  /**
   * Fetches seg feature product features
   */
  useEffect(() => {
    const periodTimeFrame: Periodicity = getPeriodForFeatures(pagesConfig, periodicity, selectedCountries);
    getSelectedSegFeatValues({
      productType: effectiveProductType,
      selectedProductGroup: selectedProductGroups,
      selectedSegFeatValues: selectedSegFeatOppValues,
      selectedSegFeat: selectedSegFeatOpp,
      initialFilters: initialFiltersState.initialFilters,
      selectedCountries,
      pagesConfig,
      selectedTimeFrame: periodTimeFrame,
    });
  }, [selectedSegFeatOppValues]);

  useEffect(() => {
    const appliedFilters = pagesConfig.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters;

    if (appliedFilters && appliedFilters.length !== 0) {
      const index = appliedFilters.findIndex((e: any) => e.filterKey === FilterKeys.PRODUCT_GROUP);
      index !== -1 ? setAppliedProductGroups(cloneDeep(appliedFilters[index].options)) : setAppliedProductGroups([]);
    } else {
      setAppliedProductGroups([]);
    }

    /**
     * Use this function to prevent setting the applied/selected countries state when the country
     * `Option` are the same with the currently selected ones.
     *
     * @param currOption - The current applied/selected countries.
     */
    const getEffectiveCountry = (currOption: Option[]) => {
      const initialAppliedCountries = getInitialAppliedCountries(
        pagesConfig,
        PageType.OPPORTUNITY_REVIEW,
        effectiveProductType,
      );
      return areOptionsTheSame(initialAppliedCountries, appliedCountries, 'value')
        ? currOption
        : initialAppliedCountries;
    };

    setAppliedCountries(getEffectiveCountry);
    setSelectedCountries(getEffectiveCountry);
  }, [pagesConfig.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters]);

  useEffect(() => {
    if (appliedProductGroups.length !== 1) {
      const priceBandFilter = pagesConfig.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters.find(
        (f) => f.filterKey === FilterKeys.PRICEBAND,
      );
      if (priceBandFilter) {
        priceBandFilter.options = [];
      }
    }
  }, [appliedProductGroups]);

  const apiTriggeringValidator = () => {
    const countries = selectedCountries.length ? selectedCountries : appliedCountries;
    return (
      !onPageFirstLoad &&
      isValidTimeFrameAndPeriod(periodicity, period) &&
      checkIfCountryExist(countries, pagesConfig, ProductType.PerformanceAnalyzer) &&
      !isFetchingApi &&
      isValidAppliedProductGroup() &&
      //to not update upon selected filter change
      isAppliedFiltersEqualToSelectedFilters(pagesConfig) &&
      !getOpportunityReviewGroup.isLoading
    );
  };

  const setDataStateToDefault = () => {
    setIsApiError(false);
    setTableData([]);
    setSwotTableTotalSize(0);
    setBubbleChartData(DEFAULT_BUBBLE_CHART_RESPONSE);
    setIsFetchingApi(false);
    setLoadingSwotTable(false);
    setLoadingBubbleChart(false);
    setAreDependenciesUpdated(false);
    setIsUpdateFeaturesApiCall(false);
    setDoPreserveSelections(false);
  };

  function getAppliedCountry(pagesConfig: any, appliedCountries: Option[]): Country | undefined {
    return pagesConfig.clientCountries
      .filter((x: Country) => appliedCountries.some((y: Option) => x.id === y.value))
      .pop();
  }

  /**
   * @remarks
   * to call table API for grouping KPIs when grouping data is changed
   * **/
  useEffect(() => {
    const fetchGroupingTableResponse = async () => {
      const appliedCountry = getAppliedCountry(pagesConfig, appliedCountries);
      const isTimeFrameExist = checkPeriodExist(defaultFilters, appliedCountry!.id, periodicity);
      if (
        !getOpportunityReviewGroup.data ||
        !isTimeFrameExist ||
        !apiTriggeringValidator() ||
        !feature ||
        tableData.length === 0
      ) {
        return;
      }
      setIsFetchingApi(true);
      await getGroupingTableResponse(feature, selectedSortingOption);
    };
    fetchGroupingTableResponse();
  }, [allGroupsForApiCalls]);

  useEffect(() => {
    if (
      allFeatureValuesOptions.length > 0 &&
      isAllFeatureValuesEmpty(allFeatureValuesOptions) &&
      apiTriggeringValidator()
    ) {
      setDataStateToDefault();
    }
  }, [areDependenciesUpdated]);

  useDebouncedEffect(() => {
    if (!areDependenciesUpdated && apiTriggeringValidator() && !isAllFeatureValuesEmpty(allFeatureValues)) {
      setIsFetchingApi(true);
      setAreDependenciesUpdated(true);
      setLoadingSwotTable(true);
      setLoadingBubbleChart(true);
    }

    if (allFeatureValues.length > 0 && isAllFeatureValuesEmpty(allFeatureValues) && apiTriggeringValidator()) {
      setDataStateToDefault();
    }
  }, [
    comparisonPeriod,
    pagesConfig.sortingOptionsOpportunityReview,
    period,
    saleType,
    selectedGrowthType,
    allFeatureValues,
    appliedProductGroups,
    isUpdateFeaturesApiCall,
    getOpportunityReviewGroup.data,
    pagesConfig.appliedFilters,
    hasSelectedCountry,
  ]);

  /**
   * To handle onChange event of feature selector
   * @param option selected option for feature
   */
  const featureHandler = useCallback(
    (option: FeatureOption) => {
      if (
        isValidTimeFrameAndPeriod(periodicity, period) &&
        isValidAppliedProductGroup() &&
        !shouldLimitProductGroup(appliedProductGroups?.length, totalSubscriptionPgs, option) &&
        !isAllFeatureValuesEmpty(allFeatureValues)
      ) {
        setIsFetchingApi(true);
        setLoadingSwotTable(true);
        setLoadingBubbleChart(true);
        setAreDependenciesUpdated(true);
      }
    },
    [allFeatureValues],
  );

  const getFeatureValueParam = (): FilterParam => {
    return {
      productType: effectiveProductType,
      selectedProductGroup: selectedProductGroups,
      initialFilters: defaultFilters,
      selectedCountries: selectedCountries,
      pagesConfig: pagesConfig,
      responseConfig: opportunityReviewResponseConfig,
      allFilters: allFilters,
      subscriptions: getSubscriptionByCountryAndProductType(
        pagesConfig,
        selectedCountries,
        ProductType.PerformanceAnalyzer,
      ),
    };
  };

  const {
    data: appliedFeatureValues,
    isLoading: isAppliedFeatureValuesLoading,
    isError: isAppliedFeatureValuesError,
  } = useFeatureValueByPGAndCountry(
    {
      countries: appliedCountries,
      productGroups: appliedProductGroups,
      periodicity,
      hasSelectedCountry: !!hasSelectedCountry,
    },
    {
      ...constructFeatureValueParam(
        getFeatureValueParam(),
        periodicity === Periodicity.Weekly ? Periodicity.Weekly : Periodicity.Monthly,
      ),
      currentTotalName: CURRENT_TOTAL_NAME,
    },
  );

  useEffect(() => {
    if (!isAppliedFeatureValuesLoading && !isAppliedFeatureValuesError && appliedFeatureValues) {
      getFeatureValuesForOR(appliedFeatureValues);
    }
  }, [appliedFeatureValues, isAppliedFeatureValuesLoading, isAppliedFeatureValuesError]);

  useEffect(() => {
    (async () => {
      // Useful when the currency is changed, to update priceband filter vals w.r.t. selected currency
      const res = await getCurrencyExchangeRate(period, pagesConfig, periodicity, initialCurrency[0]);
      pagesConfig.updateCurrencyExchangeRatePerCountry(res);
    })();
  }, [period]);

  useEffect(() => {
    const currencyExchangeRate = getExchangeRate(pagesConfig);
    const appliedFilters = cloneDeep(pagesConfig.appliedFilters);

    if (currencyExchangeRate > 0) {
      // Update PRICEBAND filter labels in applied filters
      appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters.forEach((f) => {
        if (f.filterKey === FilterKeys.PRICEBAND) {
          f.options.forEach((k) => (k.label = formatPriceBandAsPerExchangeRate(k.value, currencyExchangeRate)));
        }
      });
      const selectedFilters = cloneDeep(pagesConfig.selectedFilters);
      // Replace only the PRICEBAND filter in selectedFilters
      const appliedPriceband = appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters.find(
        (f) => f.filterKey === FilterKeys.PRICEBAND,
      );
      if (appliedPriceband) {
        selectedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters = selectedFilters[
          PageType.OPPORTUNITY_REVIEW
        ].selectedFilters.map((f) => (f.filterKey === FilterKeys.PRICEBAND ? appliedPriceband : f));
        pagesConfig.setSelectedFilters(selectedFilters);
      }

      pagesConfig.setAppliedFilters(appliedFilters);
    }
  }, [pagesConfig.currencyExchangeRatePerCountry]);

  /**
   * Get feature values for feature selector by monthly and weekly.
   *
   * @param isUpdateFeatValOptionsState is a flag to update the feature values states or not
   */
  const getFeatureValuesForOR = async (featureValues: Filters[], isUpdateFeatValOptionsState: boolean = true) => {
    const periodicityType = periodicity === Periodicity.Weekly ? Periodicity.Weekly : Periodicity.Monthly;

    const featureValueResult =
      (await getFeatureValueBySubscription(
        constructFeatureValueParam(getFeatureValueParam(), periodicityType),
        periodicity,
        CURRENT_TOTAL_NAME,
        undefined,
        setDataStateToDefault,
        featureValues,
      )) ?? [];

    setAllFeatureValuesOptions(featureValueResult);
  };

  const handleScroll = async () => {
    if (!isValidTimeFrameAndPeriod(periodicity, period) || !isValidAppliedProductGroup() || loadingSwotTable) {
      return;
    }

    if (!loadMore && !isLastPage()) {
      setIsApiError(false);
      const pageNumber = currentPage + 1;
      setLoadMore(true);

      const appliedFilters = [...pagesConfig.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters];

      const tableParam: TableParam = {
        clientId: retailerClientState.clientId,
        productType: effectiveProductType,
        productGroup: appliedProductGroups,
        countryIds: [countriesToQuery(appliedCountries)[0].id],
        period: period.value,
        comparisonPeriod: comparisonPeriod,
        periodType: periodicity,
        pageRequest: { page: pageNumber, size: defaultTableSize } as PageRequest,
        sortOption: pagesConfig.sortingOptionsOpportunityReview,
        growthType: selectedGrowthType,
        appliedFilters: appliedFilters,
        segment: feature.feature,
        dataType: feature.dataType,
        oRGroups: allGroupsForApiCalls,
      };

      const data = await getOpportunityReviewTable(tableParam);

      if (data !== null) {
        setCurrentPage(pageNumber);
        setTableData([...tableData, ...data.content.content]);
      }
      setLoadMore(false);
    }
    if (isLastPage()) {
      setDoPreserveSelections(false);
    }
  };

  useBottomScrollListener(handleScroll, { offset: 500 });

  /**
   * This function responsible to reset unapplied filter (side filter) to default filter (applied filter).
   */
  const resetSelectedFilters = () => {
    const isResetSideFilter: boolean = removeUnsavedFilters(pagesConfig);

    if (isResetSideFilter) {
      setSelectedCountries(appliedCountries);
      setSelectedProductGroups(appliedProductGroups);
      setIsUpdateFeaturesApiCall(true);
    }
  };

  /**
   * To handle the change event when select other retailer in the retailer dropdown - reset the necessary states in order to refresh the reports
   * @param subscriptions client subscription from opa policy
   */
  const onChangeHandler = async (subscriptions: ClientSubscription[]) => {
    resetSelectedFilters(); // Reset selected filter MUST be in this handler to prevent stale states (e.g. countries and pg)
    await onRetailerChange(
      subscriptions,
      {
        pagesConfig,
        appliedCountries,
        retailerClients,
        setLoadingOverlay,
        timeFrame: periodicity,
        pageName: PageName.OPPORTUNITY_REVIEW,
        selectedGrowthType,
        selectedGrowthTypeForProducts: mainFilterStateProducts.selectedGrowthType,
        setDefaultFilters,
        feature,
        updateRetailerClientAction: updateRetailerClient,
        async resetSequenceFunc({
          defaultClientId,
          subscribedCountries,
          hitlistsSubscriptions,
          hitlistsSubscribedCountries,
          defaultCountry,
          pagesConfigAllFilters,
          filtersDataBasedOnCountry,
        }) {
          /**
           * The reset sequence below is very important, this is due to the massive useEffect that we have in the page
           */
          // Do not simply move the setState below, it will trigger unintended side effects
          const marketPremiumSubscription = getSubscriptionForOtherProducts(
            ProductType.MarketPremium,
            pagesConfig,
            defaultClientId,
          );
          const marketPremiumSubscribedCountries = mapSubscribedCountries(marketPremiumSubscription);

          if (marketPremiumSubscribedCountries.length > 0) {
            pagesConfig.setMarketPremiumClientId(defaultClientId);
            pagesConfig.setMarketPremiumClientCountries(subscribedCountries);
          }
          resetSideFilter();

          pagesConfig.setClientId(defaultClientId);
          pagesConfig.setClientCountries(subscribedCountries);
          if (hitlistsSubscriptions.length !== 0) {
            pagesConfig.setHitlistsClientCountries(hitlistsSubscribedCountries);
            pagesConfig.setHitlistsClientId(defaultClientId);
          }

          setSelectedCountries(constructCountryOption(defaultCountry));

          pagesConfig.setSelectedFilters(pagesConfigAllFilters);

          setFilters(filtersDataBasedOnCountry);

          setAppliedCountries(constructCountryOption(defaultCountry));
        },
      },
      dispatch,
    );
    refetch();
  };

  const setFeatureForSelector = (feat: FeatureOption) => {
    setFeature(feat);
    setDoPreserveSelections(false);
  };

  const handleApplyTimeFramePicker = (
    givenPeriodicity: Periodicity,
    givenPeriod: BaseTimePeriod,
    givenComparison: RelativePeriod | BaseTimePeriod,
  ) => {
    resetSelectedFilters(); // Reset selected filter MUST be in this handler to prevent stale states (e.g. countries and pg)
    setDoPreserveSelections(true);
    applyTimeFramePicker({
      givenPeriod: givenPeriod,
      givenPeriodicity: givenPeriodicity,
      givenComparison: givenComparison,
      pageName: PageName.OPPORTUNITY_REVIEW,
      pageType: PageType.OPPORTUNITY_REVIEW,
      pagesConfig: pagesConfig,
      countryChanged: countryChanged,
      sideFiltersState: sideFiltersState,
      retailerClientState: retailerClientState,
      track: track,
      setTimeFrame: periodDispatchActions.updatePeriodicity,
      setPeriod: periodDispatchActions.updatePeriod,
      setRelativePeriod: periodDispatchActions.updateComparisonPeriod,
      setCustomMonthPeriodState: periodDispatchActions.updateCustomMonthsPeriodState,
      setCustomWeekPeriodState: periodDispatchActions.updateCustomWeeksPeriodState,
      setCountryChanged: setCountryChanged,
    });
  };
  const filteredScopeData = getCountryFilterPairsByTimeFrame(pgsByAppliedCountry, periodicity);

  const getSaveViewFilters = () => {
    const opportunityReviewViewState: OpportunityReviewViewState = {
      periodicity: periodicity,
      comparisonPeriod: isBaseTimePeriodFormat(comparisonPeriod)
        ? RelativePeriodLabel.PreviousPeriod
        : RelativePeriodMapping[comparisonPeriod as RelativePeriod],
      growthType: capitalizeFirstLetter(selectedGrowthType),
      appliedFilters: pagesConfig.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters,
      retailerClientState: retailerClientState,
      initialFilterState: initialFiltersState,
      appliedCountries: appliedCountries,
      hitlistRetailerClientState: hitlistsRetailerClientState,
      hitlistInitialFilterState: initialFiltersProductsState,
      productMainFilterState: mainFilterStateProducts,
      mainFilter: mainFiltersState,
      isTableView: displayTable,
      bubbleSliderCurrentPeriod: bubbleSliderCurrentPeriod,
      selectedRetailerType: retailerType,
      selectedSaleType: saleType,
      selectedFeature: feature,
      sorting: selectedSortingOption,
      currencyFilters: pagesConfig.appliedFilters[PageType.PERFORMANCE_ANALYSIS].selectedFilters,
    };
    return opportunityReviewViewState;
  };

  // props to pass local setState to restore saved views hooks
  const restoreLocalViewProps: OpportunityReviewRestoreLocalSavedViewProps = {
    productType: effectiveProductType,
    setIsSubscribed,
    setTimeFrame: periodDispatchActions.updatePeriodicity,
    setPeriod: periodDispatchActions.updatePeriod,
    setRelativePeriod: periodDispatchActions.updateComparisonPeriod,
    setGrowthOption: setSelectedGrowthType,
    setIsTableView: setDisplayTable,
    setFeature,
    setFilters,
    setSelectedCountries,
    setAppliedCountries,
    setDefaultFilters,
    setSaleType,
    setRetailerType,
    setBubbleSliderCurrentPeriod,
    setSelectedProductGroupOptions: setSelectedProductGroups,
    setAppliedProductGroupOptions: setAppliedProductGroups,
    setCustomMonthsPeriodState: periodDispatchActions.updateCustomMonthsPeriodState,
    setCustomWeeksPeriodState: periodDispatchActions.updateCustomWeeksPeriodState,
    setSelectedSortingOption,
  };

  // Call restore saved views hooks to restore the PA page views
  useRestoreOpportunityReviewSavedView(restoreLocalViewProps);

  //Get is monthly or weekly periodicity for all periodicity selected
  const isMonthlyOrWeekly =
    [Periodicity.Monthly, Periodicity.Weekly].includes(periodicity) && !checkMATOrYTDPeriodicity(period);

  const getSaveViewTrackingObject = () => {
    return {
      access: pagesConfig.access,
      locationId: appliedCountries.map((country) => country.label),
      pageName: PageName.OPPORTUNITY_REVIEW,
      itemGroup: appliedProductGroups?.map((pg) => pg.label).toString(),
      section: TrackingGeneralProp.SECTION_TRACKING_PROP,
      subSection: PageName.OPPORTUNITY_REVIEW,
    };
  };

  const createBubbleChartInsightDataFn: CreateInsightDataFn<OpportunityReviewBubbleChartInsight> = (
    collectionDataType = 'live',
  ) => {
    const { selectedBrandBubble = '' } = mainFiltersState;
    const featureOption = getFeatureSelector(allFeatureValues, feature, [], PageName.OPPORTUNITY_REVIEW);

    const bubbleChartPeriodV2 = {};
    return {
      apiParams: {
        clientId: retailerClientState.clientId,
        productType: effectiveProductType,
        productGroup: appliedProductGroups ?? [],
        country: countriesToQuery(appliedCountries),
        period: period.value,
        comparisonPeriod: comparisonPeriod as RelativePeriod,
        periodType: periodicity,
        saleType: saleType,
        segmentDataType: featureOption.dataType,
        segmentName: featureOption.feature,
        appliedFilters: allFilters,
        isSummary: false,
        ...bubbleChartPeriodV2,
      },
      retailerType: retailerType,
      growthType: selectedGrowthType,
      factRevenue: factRevenueBySelectedCurrency,
      volume: getVolumeBasedOnPeriodType(filters, periodicity),
      selectedBrandBubble,
      collectionDataType,
      isBubbleChartV2: false,
      retailerClientName: retailerClientState.clientName,
    };
  };

  useDebouncedEffect(() => {
    const selectedPg = pagesConfig.selectedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters.find(
      (f) => f.filterKey === FilterKeys.PRODUCT_GROUP,
    );
    const appliedPg = pagesConfig.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters.find(
      (f) => f.filterKey === FilterKeys.PRODUCT_GROUP,
    );
    if (selectedPg === appliedPg) {
      setShowPerformanceFeatureList(allFeatureValues);
    }
  }, [allFeatureValues]);

  const updateSorting = (sorting) => {
    setSelectedSortingOption(sorting);
    setDoPreserveSelections(true);
  };

  return (
    <div>
      <Page>
        <Page.Title>
          <div className="pt-8">
            <PageHeader
              retailerClients={retailerClients}
              onChangeHandler={onChangeHandler}
              productType={ProductType.PerformanceAnalyzer}
            >
              <div className="grid grid-cols-2">
                <div className="col-start-1">
                  <h1 className="text-heading text-4xl font-normal m-0">
                    <span>Opportunity Review</span>
                    <PageInfo
                      title={'Opportunity Review'}
                      content={
                        <section>
                          <p>
                            Switch from analysis to action - let us do the heavy lifting and make the data speak to you.
                          </p>
                          <p>
                            Combining the concepts of relative growth and your &apos;fair share&apos; to create an
                            actionable assessment for any segment, you can see not just what the data says but what it
                            means - and what you might want to do about it.
                          </p>
                        </section>
                      }
                    />
                  </h1>
                </div>
                <div className="col-start-5">
                  {isAuthorizedUser && isSubscribed && (
                    <div className="flex justify-end">
                      <OpportunityReviewCopyViewButton
                        opportunityReviewViewState={getSaveViewFilters()}
                        copyViewTracking={getSaveViewTrackingObject()}
                      />
                      <FilterToggleButton onClick={onClick} testId="filter-button" />
                    </div>
                  )}
                </div>
              </div>
            </PageHeader>
          </div>
        </Page.Title>
        <Page.Content>
          <InfoModal />
          {isAuthorizedUser && isSubscribed ? (
            <>
              <LoadingOverlay loadingOverlay={loadingOverlay} />
              <div>
                <div className="flex items-end pt-6 pb-4">
                  <div className="grid grid-flow-col auto-cols-max gap-x-3">
                    {
                      <TimeFramePickerWrapper
                        currentPeriod={period}
                        currentPeriodicity={periodicity}
                        currentComparison={comparisonPeriod}
                        periodOptions={periodOptions}
                        applyPickerCallback={handleApplyTimeFramePicker}
                        isTimeFramePickerDisabled={isFetchingApi}
                        isPeriodOptionsDisabled={false}
                        disabledPeriodicityOptions={undefined}
                        customMonthsPeriod={customMonthsPeriodState}
                        customWeeksPeriod={customWeeksPeriodState}
                        appliedCountries={appliedCountries}
                        appliedProductGroups={appliedProductGroups}
                      />
                    }
                    <ComparisonGrowthSelector
                      growthOptions={filters?.comparisonGrowth}
                      growthOption={(growth: GrowthTypes) => setSelectedGrowthType(growth)}
                      selectedGrowthOption={selectedGrowthType}
                      isDisabled={false}
                    />
                  </div>
                </div>
              </div>
              <div className="pb-4">
                <FilterSelectionNote
                  selectedPeriod={period}
                  comparisonPeriodType={comparisonPeriod}
                  selectedPeriodicity={periodicity}
                  scopeMetadata={filteredScopeData[0]?.scopeMetadata}
                />
              </div>

              {<PgAvailabilityBanner />}
              <div className="pt-4">
                <FilterChips pgListByCountry={pgsByAppliedCountry} selectedTimeFrame={periodicity} />
              </div>
              <OpportunityReviewBoardGroupProvider>
                <div className="pt-6">
                  <OpportunityReviewCard
                    data={{ bubbleChartResponse: bubbleChartData, swotTableResponse: tableData }}
                    appliedProductGroups={appliedProductGroups}
                    saleType={saleType}
                    setSaleType={setSaleType}
                    displayTable={displayTable}
                    setDisplayTable={setDisplayTable}
                    loadingBubbleChart={loadingBubbleChart}
                    loadingSwotTable={loadingSwotTable}
                    loadMore={loadMore}
                    selectedPeriod={getSelectedPeriodForBubbleChart().currentPeriod.toUpperCase()}
                    selectedTimeFrame={periodicity}
                    selectedGrowthType={selectedGrowthType}
                    factRevenue={factRevenueBySelectedCurrency}
                    resizeChart={resizeChart}
                    allFeatureValuesOptions={showPerformanceFeatureList}
                    featureFilters={updatedFeatureFilters}
                    feature={feature}
                    setFeature={setFeatureForSelector}
                    featureHandler={featureHandler}
                    onChartDownload={onChartDownload}
                    isExportButtonDisabled={isExportButtonDisabled}
                    setIsExportButtonDisabled={setIsExportButtonDisabled}
                    onChartExportReady={onChartExportReady}
                    retailerType={retailerType}
                    setRetailerType={setRetailerType}
                    setBubbleSliderCurrentPeriod={setBubbleSliderCurrentPeriod}
                    volume={getVolumeBasedOnPeriodType(filters, periodicity)}
                    isPeriodicityMonthlyOrWeekly={isMonthlyOrWeekly}
                    createInsightDataFn={createBubbleChartInsightDataFn}
                    selectedSortingOption={selectedSortingOption}
                    setSelectedSortingOption={updateSorting}
                    context={context}
                    groupContent={tableGroupData}
                    opportunityReviewState={getSaveViewFilters()}
                    doPreserveSelections={doPreserveSelections}
                    setDoPreserveSelections={setDoPreserveSelections}
                    totalSize={swotTableTotalSize - 1}
                    isApiError={isApiError}
                  />
                  {displayTable && (
                    <>
                      <OpportunityReviewBoardActionBar
                        totalProductGroups={swotTableTotalSize - 1}
                        context={context}
                        isEditable={isEditable}
                        setIsEditable={setIsEditable}
                      />
                      <CreateOpportunityReviewBoardModalGroup context={context} sortOrder={getNewSortValues} />
                      <ShareOpportunityReviewBoardModalGroup
                        context={context}
                        saveViewFilter={getSaveViewFilters()}
                        retailerClients={retailerClients}
                      />
                      <DeleteOpportunityReviewBoardModalGroup context={context} />
                    </>
                  )}
                </div>
              </OpportunityReviewBoardGroupProvider>
            </>
          ) : (
            Unauthorized()
          )}
        </Page.Content>
        <Page.Footer> </Page.Footer>
      </Page>
    </div>
  );
};

export default OpportunityReview;
